/**
 * Excel table helpers
 */

import { logger } from "../shared/logger";
import { runExcel } from "./runExcel";

export type TableTarget =
  | { kind: "activeCell" }
  | { kind: "sheet"; sheetName: string; anchorCell?: string };

export interface WriteTableOptions {
  target?: TableTarget;
  sheetName?: string; // Legacy support - will be ignored if target is provided
  tableName: string;
  headers: string[];
  rows: (string | number | boolean | null)[][];
  columnFormats?: Record<string, string>; // Map column names to Excel number format strings
}

export interface WriteTableResult {
  sheetName: string;
  tableName: string; // Base name (for display)
  actualTableName: string; // Effective name used in Excel
  anchorA1: string; // A1-style address without sheet prefix
  moved: boolean;
  reason?: "cell_not_empty" | "overlap_avoidance";
}

// Helper to get Excel column name (A, B, ..., Z, AA, AB, ...)
function toExcelColumnName(colIndex1Based: number): string {
  // 1 -> A, 26 -> Z, 27 -> AA, etc.
  let n = colIndex1Based;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/**
 * Slugify sheet name for use in table name suffix
 * - Replace non [A-Za-z0-9_] with "_"
 * - Collapse repeated underscores
 * - Trim
 * - Limit to 24 chars
 * - If empty, return "sheet"
 */
function slugifySheetName(name: string): string {
  let slug = name
    .replace(/[^A-Za-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .trim();
  
  if (slug.length === 0) {
    slug = "sheet";
  }
  
  // Limit to 24 chars
  if (slug.length > 24) {
    slug = slug.substring(0, 24);
  }
  
  return slug;
}

/**
 * Generate effective table name that includes sheet suffix
 * This ensures tables on different sheets don't collide
 */
function effectiveTableName(baseTableName: string, sheetName: string): string {
  const slug = slugifySheetName(sheetName);
  const effective = baseTableName + "__" + slug;
  
  // Excel table names have a max length, ensure we don't exceed reasonable limits
  // Keep total under 100 chars
  if (effective.length > 100) {
    const maxBaseLength = 100 - slug.length - 2; // 2 for "__"
    const truncatedBase = baseTableName.substring(0, Math.max(1, maxBaseLength));
    return truncatedBase + "__" + slug;
  }
  
  return effective;
}

/**
 * Check if two ranges overlap
 */
function rangesOverlap(
  r1Row: number,
  r1RowCount: number,
  r1Col: number,
  r1ColCount: number,
  r2Row: number,
  r2RowCount: number,
  r2Col: number,
  r2ColCount: number
): boolean {
  return !(
    r1Row + r1RowCount <= r2Row ||
    r2Row + r2RowCount <= r1Row ||
    r1Col + r1ColCount <= r2Col ||
    r2Col + r2ColCount <= r1Col
  );
}

/**
 * Write table to Excel worksheet
 * Supports active cell insertion or sheet-based insertion with overlap avoidance
 */
export async function writeTable(options: WriteTableOptions): Promise<WriteTableResult> {
  const { target, sheetName: legacySheetName, tableName, headers, rows, columnFormats } = options;

  try {
    return await runExcel(async (context): Promise<WriteTableResult> => {
    logger.log("Tables", "writeTable starting", "table=" + tableName);

    // Determine destination worksheet and anchor cell
    let sheet: Excel.Worksheet;
    let anchorRow: number;
    let anchorCol: number;
    let moved = false;
    let moveReason: "cell_not_empty" | "overlap_avoidance" | undefined;

    if (target?.kind === "activeCell") {
      // Use active cell as anchor
      const activeCell = context.workbook.getActiveCell();
      sheet = activeCell.worksheet;
      activeCell.load(["rowIndex", "columnIndex"]);
      await context.sync();
      anchorRow = activeCell.rowIndex; // 0-based
      anchorCol = activeCell.columnIndex; // 0-based
      logger.log("Tables", "using active cell", "row=" + (anchorRow + 1) + " col=" + (anchorCol + 1));
    } else if (target?.kind === "sheet") {
      // Use specified sheet
      const targetSheetName = target.sheetName;
      try {
        sheet = context.workbook.worksheets.getItem(targetSheetName);
        sheet.load("name");
        await context.sync();
        logger.log("Tables", "found existing sheet", targetSheetName);
      } catch (error) {
        // Sheet doesn't exist - do NOT create by default for sheet target
        throw new Error("Sheet '" + targetSheetName + "' does not exist. Please create it first.");
      }

      if (target.anchorCell) {
        // Use specified anchor cell
        const anchorRange = sheet.getRange(target.anchorCell);
        anchorRange.load(["rowIndex", "columnIndex"]);
        await context.sync();
        anchorRow = anchorRange.rowIndex; // 0-based
        anchorCol = anchorRange.columnIndex; // 0-based
        logger.log("Tables", "using anchor cell", target.anchorCell);
      } else {
        // Default to A1
        anchorRow = 0;
        anchorCol = 0;
        logger.log("Tables", "using default anchor", "A1");
      }
    } else {
      // Legacy: use sheetName (backward compatibility)
      const sheetName = legacySheetName || "OptLab";
      try {
        sheet = context.workbook.worksheets.getItem(sheetName);
        sheet.load("name");
        await context.sync();
        logger.log("Tables", "found existing sheet (legacy)", sheetName);
      } catch (error) {
        // Legacy behavior: create sheet if missing
        logger.log("Tables", "sheet not found, creating (legacy)", sheetName);
        sheet = context.workbook.worksheets.add(sheetName);
        await context.sync();
        logger.log("Tables", "created sheet (legacy)", sheetName);
      }
      anchorRow = 0;
      anchorCol = 0;
      logger.log("Tables", "using legacy mode", "A1");
    }

    // Ensure sheet is loaded
    sheet.load("name");
    await context.sync();
    const finalSheetName = sheet.name;

    // Determine effective table name (per-sheet to avoid collisions)
    // Use effective name for activeCell and sheet targets; legacy uses base name
    const useEffectiveName = target?.kind === "activeCell" || target?.kind === "sheet";
    const actualTableName = useEffectiveName
      ? effectiveTableName(tableName, finalSheetName)
      : tableName;

    logger.log("Excel", "writeTable base=" + tableName + " actual=" + actualTableName + " sheet=" + finalSheetName, "info");

    // Prepare data: headers + rows
    const allData: (string | number | boolean | null)[][] = [headers, ...rows];
    const totalRows = allData.length; // Includes header
    const totalCols = headers.length;
    logger.log("Tables", "data prepared", "rows=" + totalRows + " cols=" + totalCols);

    // Check if table already exists by effective name on this sheet
    let existingTable: Excel.Table | null = null;
    try {
      existingTable = sheet.tables.getItemOrNullObject(actualTableName);
      existingTable.load("name");
      await context.sync();
      if (existingTable.name) {
        logger.log("Tables", "table exists, will update", actualTableName);
      } else {
        existingTable = null;
        logger.log("Tables", "table does not exist, will create", actualTableName);
      }
    } catch (error) {
      existingTable = null;
      logger.log("Tables", "table does not exist, will create", actualTableName);
    }

    let table: Excel.Table;
    let finalAnchorRow: number;
    let finalAnchorCol: number;
    let anchorAddress: string;

    if (existingTable) {
      // Update existing table: resize and update values
      logger.log("Excel", "writeTable update table=" + actualTableName + " rows=" + rows.length, "info");
      
      // Get anchor position
      const anchor = existingTable.getRange();
      anchor.load(["rowIndex", "columnIndex"]);
      await context.sync();
      
      finalAnchorRow = anchor.rowIndex; // 0-based
      finalAnchorCol = anchor.columnIndex; // 0-based
      
      // Create new range with correct size
      const start = sheet.getCell(finalAnchorRow, finalAnchorCol);
      const newRange = start.getResizedRange(totalRows - 1, totalCols - 1);
      
      // Set values first
      newRange.values = allData;
      await context.sync();
      
      // Resize table to match new range
      existingTable.resize(newRange);
      await context.sync();
      logger.log("Tables", "table resized and updated", "rows=" + totalRows + " cols=" + totalCols);
      
      // Apply column formats if provided
      if (columnFormats && Object.keys(columnFormats).length > 0) {
        try {
          logger.log("Tables", "applying column formats", Object.keys(columnFormats).join(","));
          
          const formatEntries: Array<{ columnIndex: number; format: string; range: Excel.Range }> = [];
          
          for (const [columnName, formatString] of Object.entries(columnFormats)) {
            const columnIndex = headers.indexOf(columnName);
            if (columnIndex >= 0) {
              const range = existingTable.columns.getItemAt(columnIndex).getDataBodyRange();
              range.load("rowCount");
              formatEntries.push({ columnIndex, format: formatString, range });
            }
          }

          await context.sync();

          for (const { range, format } of formatEntries) {
            const rowCount = range.rowCount;
            if (rowCount > 0) {
              const formatArray: string[][] = [];
              for (let i = 0; i < rowCount; i++) {
                formatArray.push([format]);
              }
              (range as Excel.Range & { numberFormat: string[][] }).numberFormat = formatArray;
            }
          }

          await context.sync();
          logger.log("Tables", "column formats applied", actualTableName);
        } catch (error) {
          logger.error("Tables", "failed to apply column formats", error);
        }
      }
      
      // Get anchor address (A1 style, no sheet prefix)
      const anchorCell = sheet.getCell(finalAnchorRow, finalAnchorCol);
      anchorCell.load("address");
      await context.sync();
      const fullAddress = String(anchorCell.address);
      anchorAddress = fullAddress.includes("!") ? fullAddress.split("!").pop()! : fullAddress;
      
      table = existingTable;
    } else {
      // Create new table - find safe insertion location
      let candidateRow = anchorRow;
      let candidateCol = anchorCol;

      // Check if anchor cell is safe (empty and not in a table)
      let anchorCellSafe = false;
      try {
        const anchorCell = sheet.getCell(anchorRow, anchorCol);
        anchorCell.load("values");
        await context.sync();
        
        // Check if cell has a value
        const cellValue = anchorCell.values[0][0];
        const isEmpty = cellValue === null || cellValue === undefined || cellValue === "";
        
        if (isEmpty) {
          // Check if cell is within an existing table
          const tables = sheet.tables;
          tables.load("items");
          await context.sync();
          
          let inTable = false;
          if (tables.items && tables.items.length > 0) {
            const tableRanges: Array<{ row: number; rowCount: number; col: number; colCount: number }> = [];
            for (let i = 0; i < tables.items.length; i++) {
              const tbl = tables.items[i];
              const tblRange = tbl.getRange();
              tblRange.load(["rowIndex", "rowCount", "columnIndex", "columnCount"]);
              tableRanges.push({
                row: tblRange.rowIndex,
                rowCount: tblRange.rowCount,
                col: tblRange.columnIndex,
                colCount: tblRange.columnCount,
              });
            }
            await context.sync();
            
            // Check if anchor cell is within any table
            for (const tblRange of tableRanges) {
              if (
                anchorRow >= tblRange.row &&
                anchorRow < tblRange.row + tblRange.rowCount &&
                anchorCol >= tblRange.col &&
                anchorCol < tblRange.col + tblRange.colCount
              ) {
                inTable = true;
                break;
              }
            }
          }
          
          anchorCellSafe = !inTable;
        }
      } catch (error) {
        logger.log("Tables", "could not check anchor cell safety", String(error));
        anchorCellSafe = false;
      }

      // If anchor cell is not safe, find a safe location
      if (!anchorCellSafe && target?.kind === "activeCell") {
        moved = true;
        moveReason = "cell_not_empty";
        logger.log("Tables", "anchor cell not safe, finding safe location");
        
        // Find location below used range, preserving selected column
        try {
          const used = sheet.getUsedRangeOrNullObject();
          used.load(["rowIndex", "rowCount"]);
          await context.sync();
          if (used && used.rowCount !== undefined && used.rowCount > 0) {
            candidateRow = used.rowIndex + used.rowCount + 2; // 2 blank rows spacing
            candidateCol = anchorCol; // Preserve selected column
            logger.log("Tables", "found safe location below used range", "row=" + (candidateRow + 1) + " col=" + (candidateCol + 1));
          } else {
            candidateRow = 0;
            candidateCol = anchorCol; // Preserve selected column
            logger.log("Tables", "sheet empty, using row 1 col=" + (candidateCol + 1));
          }
        } catch (error) {
          candidateRow = 0;
          candidateCol = anchorCol; // Preserve selected column
          logger.log("Tables", "could not determine used range, using row 1 col=" + (candidateCol + 1));
        }
      }

      // Check for table overlap and adjust if needed (BEFORE creating table)
      let iterations = 0;
      const maxIterations = 20;
      let conflictingTableName: string | null = null;
      
      while (iterations < maxIterations) {
        let hasOverlap = false;
        
        try {
          const tables = sheet.tables;
          tables.load("items");
          await context.sync();
          
          if (tables.items && tables.items.length > 0) {
            // Load table names and ranges
            const tableInfos: Array<{ name: string; row: number; rowCount: number; col: number; colCount: number }> = [];
            for (let i = 0; i < tables.items.length; i++) {
              const tbl = tables.items[i];
              tbl.load("name");
              const tblRange = tbl.getRange();
              tblRange.load(["rowIndex", "rowCount", "columnIndex", "columnCount"]);
            }
            await context.sync();
            
            // Now extract values after sync
            for (let i = 0; i < tables.items.length; i++) {
              const tbl = tables.items[i];
              const tblRange = tbl.getRange();
              tableInfos.push({
                name: tbl.name,
                row: tblRange.rowIndex,
                rowCount: tblRange.rowCount,
                col: tblRange.columnIndex,
                colCount: tblRange.columnCount,
              });
            }
            
            // Check overlap with candidate range, EXCLUDING self
            for (const tblInfo of tableInfos) {
              // Skip the table we're about to create/update (exclude self)
              if (tblInfo.name === actualTableName) {
                continue;
              }
              
              if (
                rangesOverlap(
                  candidateRow,
                  totalRows,
                  candidateCol,
                  totalCols,
                  tblInfo.row,
                  tblInfo.rowCount,
                  tblInfo.col,
                  tblInfo.colCount
                )
              ) {
                hasOverlap = true;
                conflictingTableName = tblInfo.name;
                // Move candidate below this table, preserving column
                candidateRow = tblInfo.row + tblInfo.rowCount + 2; // 2 blank rows
                // Keep candidateCol unchanged (preserve selected column)
                if (!moved) {
                  moved = true;
                  moveReason = "overlap_avoidance";
                }
                logger.log("Tables", "overlap detected with " + tblInfo.name + ", moving to row=" + (candidateRow + 1) + " col=" + (candidateCol + 1));
                break;
              }
            }
          }
        } catch (error) {
          logger.log("Tables", "could not check for overlap", String(error));
          break; // Proceed with candidate location
        }
        
        if (!hasOverlap) {
          break;
        }
        
        iterations++;
      }

      if (iterations >= maxIterations) {
        throw new Error(
          "Unable to find a safe location for table " +
            actualTableName +
            " after " +
            maxIterations +
            " iterations. Candidate was R" +
            (candidateRow + 1) +
            "C" +
            (candidateCol + 1) +
            (conflictingTableName ? " (conflicts with " + conflictingTableName + ")" : "")
        );
      }

      finalAnchorRow = candidateRow;
      finalAnchorCol = candidateCol;

      // Create range and set values
      const range = sheet.getCell(finalAnchorRow, finalAnchorCol).getResizedRange(totalRows - 1, totalCols - 1);
      range.values = allData;
      await context.sync();
      logger.log("Tables", "values set", "row=" + (finalAnchorRow + 1) + " col=" + (finalAnchorCol + 1));

      // Create table
      table = sheet.tables.add(range, true);
      table.name = actualTableName;
      await context.sync();
      logger.log("Tables", "table created", "name=" + actualTableName);

      // Apply column formats if provided
      if (columnFormats && Object.keys(columnFormats).length > 0) {
        try {
          logger.log("Tables", "applying column formats", Object.keys(columnFormats).join(","));
          
          const formatEntries: Array<{ columnIndex: number; format: string; range: Excel.Range }> = [];
          
          for (const [columnName, formatString] of Object.entries(columnFormats)) {
            const columnIndex = headers.indexOf(columnName);
            if (columnIndex >= 0) {
              const range = table.columns.getItemAt(columnIndex).getDataBodyRange();
              range.load("rowCount");
              formatEntries.push({ columnIndex, format: formatString, range });
            }
          }

          await context.sync();

          for (const { range, format } of formatEntries) {
            const rowCount = range.rowCount;
            if (rowCount > 0) {
              const formatArray: string[][] = [];
              for (let i = 0; i < rowCount; i++) {
                formatArray.push([format]);
              }
              (range as Excel.Range & { numberFormat: string[][] }).numberFormat = formatArray;
            }
          }

          await context.sync();
          logger.log("Tables", "column formats applied", actualTableName);
        } catch (error) {
          logger.error("Tables", "failed to apply column formats", error);
        }
      }

      // Get anchor address (A1 style, no sheet prefix)
      const anchorCell = sheet.getCell(finalAnchorRow, finalAnchorCol);
      anchorCell.load("address");
      await context.sync();
      const fullAddress = String(anchorCell.address);
      anchorAddress = fullAddress.includes("!") ? fullAddress.split("!").pop()! : fullAddress;
    }

    // Format header row (with error handling)
    try {
      logger.log("Tables", "formatting header row");
      const headerRange = table.getHeaderRowRange();
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = "#D3D3D3";
      await context.sync();
      logger.log("Tables", "header row formatted", tableName);
    } catch (error) {
      logger.error("Tables", "failed to format header row", error);
      // Continue even if formatting fails
    }

    // Dev-only: verify no overlap after creation (exclude self)
    if (process.env.NODE_ENV !== "production") {
      try {
        const tableRange = table.getRange();
        tableRange.load(["rowIndex", "rowCount", "columnIndex", "columnCount"]);
        await context.sync();
        
        const tables = sheet.tables;
        tables.load("items");
        await context.sync();
        
        if (tables.items && tables.items.length > 1) {
          // Check against other tables (exclude self by actualTableName)
          for (let i = 0; i < tables.items.length; i++) {
            const otherTable = tables.items[i];
            // Skip self using actualTableName (not base tableName)
            if (otherTable.name === actualTableName) {
              continue;
            }
            
            const otherRange = otherTable.getRange();
            otherRange.load(["rowIndex", "rowCount", "columnIndex", "columnCount"]);
            await context.sync();
            
            if (
              rangesOverlap(
                tableRange.rowIndex,
                tableRange.rowCount,
                tableRange.columnIndex,
                tableRange.columnCount,
                otherRange.rowIndex,
                otherRange.rowCount,
                otherRange.columnIndex,
                otherRange.columnCount
              )
            ) {
              throw new Error(
                "Table overlap detected: " +
                  actualTableName +
                  " overlaps with " +
                  otherTable.name +
                  " at anchor R" +
                  (tableRange.rowIndex + 1) +
                  "C" +
                  (tableRange.columnIndex + 1) +
                  ". This should not happen."
              );
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes("overlap")) {
          throw error; // Re-throw overlap errors
        }
        // Ignore other errors in dev check
        logger.log("Tables", "dev overlap check skipped", String(error));
      }
    }

    // Select the anchor cell so the user sees where the table was written
    try {
      const focusCell = sheet.getCell(finalAnchorRow, finalAnchorCol);
      focusCell.select();
      await context.sync();
      logger.log("Tables", "selected anchor cell", anchorAddress);
    } catch (error) {
      logger.error("Tables", "failed to select anchor cell", error);
    }

    logger.log("Excel", "writeTable table=" + tableName + " sheet=" + finalSheetName + " anchor=" + anchorAddress + (moved ? " (moved)" : ""), "info");
    logger.log("Tables", "writeTable completed successfully", tableName);

    return {
      sheetName: finalSheetName,
      tableName, // Base name for display
      actualTableName, // Effective name used in Excel
      anchorA1: anchorAddress, // A1-style address without sheet prefix
      moved,
      reason: moveReason,
    };
    });
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error("Tables", "writeTable failed", error);
    throw new Error("Failed to write table " + tableName + ": " + errorMsg);
  }
}
